import { P, FF } from "../../styles/tokens";
import { fmt, sum, sumFijosReal } from "../../utils/format";
import { MESES, MESES_CORTO } from "../../constants/months";
import { catIcon, catColor } from "../../constants/categories";
import { Card, Heading } from "../ui";
import { useFinancial } from "../../contexts/FinancialContext";
import { useNavigation } from "../../contexts/NavigationContext";
import { useUI } from "../../contexts/UIContext";
import { isRecurrenteGhost } from "../../hooks/useMonthData";

/* ─── helpers ─── */
const sec = (title) => (
  <div style={{ fontSize: 13, fontWeight: 800, color: P.textSm, fontFamily: FF,
    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
    {title}
  </div>
);

/* ══════════════════════════════════════════════
   SECCIÓN 1 — Scorecard Anual
═══════════════════════════════════════════════ */
function ScoreAnual({ anualTotals, totalDeudaTDCGlobal, currentMonth, mobile }) {
  const { ingresos, gastos, ahorro } = anualTotals;
  const tasa = ingresos > 0 ? Math.round((ahorro / ingresos) * 100) : 0;
  const tasaColor = tasa < 10 ? P.danger.text : tasa < 20 ? P.warn.text : P.ingresos.text;
  const tasaBg   = tasa < 10 ? P.danger.pastel : tasa < 20 ? P.warn.pastel : P.ingresos.pastel;
  const mesesReales = currentMonth + 1;
  const promMensual = mesesReales > 0 ? Math.round(ahorro / mesesReales) : 0;
  const pctGastado  = ingresos > 0 ? Math.round((gastos  / ingresos) * 100) : 0;
  const pctAhorrado = ingresos > 0 ? Math.round((ahorro  / ingresos) * 100) : 0;

  const cards = [
    { label: "Ingresado",   value: ingresos, ctx: `${mesesReales} mes${mesesReales>1?"es":""}`, token: P.ingresos },
    { label: "Gastado",     value: gastos,   ctx: `${pctGastado}% del ingreso`,             token: P.extras },
    { label: "Ahorrado",    value: ahorro,   ctx: `Promedio $${Math.round(promMensual/1000)}k/mes`, token: { text: tasaColor, pastel: tasaBg } },
    { label: "Deuda TDC",   value: totalDeudaTDCGlobal, ctx: "saldo total tarjetas",        token: P.danger },
  ];

  return (
    <Card style={{ marginBottom: 16 }}>
      {sec("Resumen del año")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {cards.map(({ label, value, ctx, token }) => (
          <div key={label} style={{
            background: token.pastel || P.bg, borderRadius: 12, padding: "14px 16px",
            border: `1.5px solid ${P.border}`,
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: P.textSm, fontFamily: FF,
              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
              {label}
            </div>
            <div style={{ fontSize: mobile ? 16 : 20, fontWeight: 900, color: token.text || P.text,
              fontFamily: FF, letterSpacing: "-0.5px", lineHeight: 1.1, marginBottom: 4 }}>
              {fmt(value)}
            </div>
            <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>{ctx}</div>
          </div>
        ))}
      </div>

      {/* Barra proporción gastado vs ahorrado */}
      <div style={{ marginTop: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: P.textSm, fontFamily: FF }}>Gastado {pctGastado}%</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: tasaColor, fontFamily: FF }}>
            Tasa ahorro {tasa}%
          </span>
        </div>
        <div style={{ height: 10, background: P.bg, borderRadius: 99, overflow: "hidden", display: "flex", border: `1px solid ${P.border}` }}>
          <div style={{ width: `${Math.min(pctGastado, 100)}%`, background: P.extras.mid, borderRadius: "99px 0 0 99px", transition: "width .4s" }} />
          <div style={{ width: `${Math.min(Math.max(pctAhorrado, 0), 100 - Math.min(pctGastado, 100))}%`,
            background: tasa >= 20 ? P.ingresos.mid : tasa >= 10 ? P.warn.mid : P.danger.mid,
            borderRadius: "0 99px 99px 0", transition: "width .4s" }} />
        </div>
        <div style={{ display: "flex", gap: 14, marginTop: 6 }}>
          {[
            { color: P.extras.mid, label: "Gastos" },
            { color: tasa >= 20 ? P.ingresos.mid : tasa >= 10 ? P.warn.mid : P.danger.mid, label: "Ahorro" },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
              <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ══════════════════════════════════════════════
   SECCIÓN 2 — Tasa de ahorro por mes
═══════════════════════════════════════════════ */
function TasaAhorro({ anualData, currentMonth, data }) {
  const meses = anualData.slice(0, currentMonth + 1).map((row, i) => {
    const ing = row.ingresos;
    const tasa = ing > 0 ? (ing - row.fijos - row.extras) / ing * 100 : 0;
    return { ...row, tasa: Math.round(tasa), i };
  });

  const tasasPositivas = meses.filter(m => m.tasa > 0);
  const promedio = meses.length > 0 ? Math.round(meses.reduce((s, m) => s + m.tasa, 0) / meses.length) : 0;
  const mejor    = tasasPositivas.length > 0
    ? tasasPositivas.reduce((b, m) => m.tasa > b.tasa ? m : b, tasasPositivas[0])
    : null;

  const maxAbs = Math.max(...meses.map(m => Math.abs(m.tasa)), 30);

  return (
    <Card style={{ marginBottom: 16 }}>
      {sec("Tasa de ahorro por mes")}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {meses.map(({ mes, tasa, i }) => {
          const positivo  = tasa >= 0;
          const barWidth  = Math.round(Math.abs(tasa) / maxAbs * 70); // max 70% del ancho
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 30, fontSize: 11, color: P.textSm, fontFamily: FF, flexShrink: 0 }}>{mes}</div>
              {/* barra desde el 20% */}
              <div style={{ flex: 1, position: "relative", height: 18 }}>
                {/* línea de meta 20% */}
                <div style={{
                  position: "absolute", left: "20%", top: 0, bottom: 0,
                  width: 1, background: P.borderMd, borderLeft: `1px dashed ${P.borderMd}`, zIndex: 1,
                }} />
                <div style={{
                  position: "absolute", top: 3, bottom: 3, left: 0,
                  width: `${barWidth}%`,
                  background: positivo ? P.ingresos.mid : P.danger.mid,
                  borderRadius: 4, opacity: 0.85, zIndex: 0,
                }} />
              </div>
              <div style={{ width: 36, textAlign: "right", fontSize: 11, fontWeight: 700,
                color: positivo ? P.ingresos.text : P.danger.text, fontFamily: FF, flexShrink: 0 }}>
                {tasa}%
              </div>
              <div style={{ width: 72, textAlign: "right", fontSize: 10, color: P.textXs,
                fontFamily: FF, flexShrink: 0 }}>
                {fmt(anualData[i].ingresos - anualData[i].fijos - anualData[i].extras)}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${P.border}`,
        display: "flex", gap: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: P.textSm, fontFamily: FF }}>
          Promedio: <b style={{ color: promedio >= 20 ? P.ingresos.text : promedio >= 10 ? P.warn.text : P.danger.text }}>{promedio}%</b>
        </span>
        {mejor && (
          <span style={{ fontSize: 11, color: P.textSm, fontFamily: FF }}>
            Mejor: <b style={{ color: P.ingresos.text }}>{MESES[mejor.i]} ({mejor.tasa}%)</b>
          </span>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 10, height: 10, borderLeft: `2px dashed ${P.borderMd}` }} />
          <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>Meta 20%</span>
        </div>
      </div>
    </Card>
  );
}

/* ══════════════════════════════════════════════
   SECCIÓN 3 — Top categorías del año
═══════════════════════════════════════════════ */
function TopCategorias({ data, currentMonth }) {
  // Agrupar todos los extras reales (no ghost) de los meses hasta currentMonth
  const catMap = {};
  for (let i = 0; i <= currentMonth; i++) {
    (data[i]?.extras || []).forEach(e => {
      if (isRecurrenteGhost(e)) return;
      const cat = e.cat || "Otros";
      catMap[cat] = (catMap[cat] || 0) + (Number(e.monto) || 0);
    });
  }

  const sorted = Object.entries(catMap).sort((a, b) => b[1] - a[1]);
  const top5   = sorted.slice(0, 5);
  const otros  = sorted.slice(5).reduce((s, [, v]) => s + v, 0);
  const items  = otros > 0 ? [...top5, ["Otros", otros]] : top5;
  const total  = items.reduce((s, [, v]) => s + v, 0);

  if (items.length === 0) {
    return (
      <Card style={{ marginBottom: 16 }}>
        {sec("Top categorías del año")}
        <div style={{ fontSize: 12, color: P.textXs, fontFamily: FF }}>Sin gastos registrados.</div>
      </Card>
    );
  }

  const maxVal = items[0][1];

  return (
    <Card style={{ marginBottom: 16 }}>
      {sec("Top categorías del año")}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map(([cat, val]) => {
          const pct     = total > 0 ? Math.round(val / total * 100) : 0;
          const barPct  = maxVal > 0 ? Math.round(val / maxVal * 100) : 0;
          const color   = catColor(cat);
          return (
            <div key={cat}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 14 }}>{catIcon(cat)}</span>
                <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: P.text, fontFamily: FF }}>{cat}</span>
                <span style={{ fontSize: 12, fontWeight: 900, color: P.text, fontFamily: FF }}>{fmt(val)}</span>
                <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF, width: 30, textAlign: "right" }}>{pct}%</span>
              </div>
              <div style={{ height: 7, background: P.bg, borderRadius: 99, overflow: "hidden", border: `1px solid ${P.border}` }}>
                <div style={{ width: `${barPct}%`, height: "100%", background: color, borderRadius: 99, transition: "width .4s" }} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${P.border}`,
        fontSize: 11, color: P.textXs, fontFamily: FF }}>
        Total extras registrados: <b style={{ color: P.text }}>{fmt(total)}</b>
      </div>
    </Card>
  );
}

/* ══════════════════════════════════════════════
   SECCIÓN 4 — Tendencia gasto mensual (barras verticales CSS)
═══════════════════════════════════════════════ */
function TendenciaGasto({ anualData, currentMonth }) {
  const meses = anualData.slice(0, currentMonth + 1);
  const maxIng = Math.max(...meses.map(m => m.ingresos), 1);
  const chartH = 120;

  return (
    <Card style={{ marginBottom: 16 }}>
      {sec("Tendencia de gasto mensual")}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: chartH + 24, overflowX: "auto" }}>
        {meses.map((row, i) => {
          const hFij = Math.round((row.fijos  / maxIng) * chartH);
          const hExt = Math.round((row.extras / maxIng) * chartH);
          const hTotal = hFij + hExt;
          return (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center",
              flex: "0 0 auto", minWidth: 28 }}>
              {/* barra */}
              <div style={{ display: "flex", flexDirection: "column-reverse", width: "100%",
                height: chartH, justifyContent: "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", width: "100%",
                  height: Math.max(hTotal, 2), justifyContent: "flex-end", overflow: "hidden",
                  borderRadius: "4px 4px 0 0" }}>
                  <div style={{ height: Math.max(hExt, 0), background: P.extras.mid, width: "100%" }} />
                  <div style={{ height: Math.max(hFij, 0), background: P.fijos.mid, width: "100%" }} />
                </div>
              </div>
              {/* etiqueta */}
              <div style={{ fontSize: 9, color: P.textXs, fontFamily: FF, marginTop: 4,
                textAlign: "center", whiteSpace: "nowrap" }}>
                {row.mes}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
        {[
          { color: P.fijos.mid,  label: "Fijos" },
          { color: P.extras.mid, label: "Extras" },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
            <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>{label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ══════════════════════════════════════════════
   SECCIÓN 5 — Proyección a diciembre
═══════════════════════════════════════════════ */
function Proyeccion({ anualTotals, currentMonth, recurrentes, msiCompras }) {
  if (currentMonth < 2) return null; // necesita 3+ meses

  const mesesReales = currentMonth + 1;
  const promIng   = anualTotals.ingresos / mesesReales;
  const promGasto = anualTotals.gastos   / mesesReales;
  const promAh    = (anualTotals.ingresos - anualTotals.gastos) / mesesReales;

  const mesesRestantes = 12 - mesesReales;
  const ingAnual  = Math.round(anualTotals.ingresos + promIng   * mesesRestantes);
  const gastAnual = Math.round(anualTotals.gastos   + promGasto * mesesRestantes);
  const ahAnual   = Math.round(anualTotals.ingresos - anualTotals.gastos + promAh * mesesRestantes);
  const tasaProy  = ingAnual > 0 ? Math.round(ahAnual / ingAnual * 100) : 0;
  const tasaColor = tasaProy < 10 ? P.danger.text : tasaProy < 20 ? P.warn.text : P.ingresos.text;

  // Cuánto más hay que ahorrar por mes para llegar al 20%
  const ah20 = Math.round(ingAnual * 0.2);
  const faltaTotal = Math.max(ah20 - ahAnual, 0);
  const faltaMes   = mesesRestantes > 0 ? Math.round(faltaTotal / mesesRestantes) : 0;

  // Compromisos: recurrentes * meses restantes + MSI pendiente
  const totalRecMes = (recurrentes || []).reduce((s, r) => s + (Number(r.monto) || 0), 0);
  const compromisoRec = totalRecMes * mesesRestantes;

  const msiPendiente = (msiCompras || []).reduce((s, c) => {
    const pagadas = Object.values(c.pagos || {}).filter(p => p.pagado).length;
    const restantes = Math.max((c.meses || 0) - pagadas, 0);
    return s + restantes * (Number(c.cuota) || 0);
  }, 0);

  const compromisoTotal = compromisoRec + msiPendiente;

  const rows = [
    { label: "Ingreso anual estimado",  value: ingAnual,       token: P.ingresos },
    { label: "Gasto anual estimado",    value: gastAnual,      token: P.extras },
    { label: "Ahorro estimado",         value: ahAnual,        token: ahAnual >= 0 ? P.ahorro : P.danger },
    { label: "Tasa proyectada",         value: `${tasaProy}%`, token: { text: tasaColor } },
  ];

  return (
    <Card style={{ marginBottom: 16 }}>
      {sec("Proyección a diciembre")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        {rows.map(({ label, value, token }) => (
          <div key={label} style={{ background: P.bg, borderRadius: 10, padding: "12px 14px",
            border: `1.5px solid ${P.border}` }}>
            <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: token.text || P.text, fontFamily: FF }}>
              {typeof value === "string" ? value : fmt(value)}
            </div>
          </div>
        ))}
      </div>

      {faltaMes > 0 && (
        <div style={{ background: P.warn.pastel, borderRadius: 10, padding: "10px 14px",
          border: `1.5px solid ${P.warn.tint}`, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: P.warn.text, fontFamily: FF, fontWeight: 600 }}>
            Si ahorras <b>{fmt(faltaMes)}</b> más/mes, llegas al 20%
          </div>
        </div>
      )}

      {compromisoTotal > 0 && (
        <div style={{ background: P.fijos.pastel, borderRadius: 10, padding: "10px 14px",
          border: `1.5px solid ${P.fijos.tint}` }}>
          <div style={{ fontSize: 11, color: P.fijos.text, fontFamily: FF, fontWeight: 600, marginBottom: 4 }}>
            Compromisos restantes del año
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {compromisoRec > 0 && (
              <span style={{ fontSize: 11, color: P.fijos.text, fontFamily: FF }}>
                Recurrentes: <b>{fmt(compromisoRec)}</b>
              </span>
            )}
            {msiPendiente > 0 && (
              <span style={{ fontSize: 11, color: P.fijos.text, fontFamily: FF }}>
                MSI pendiente: <b>{fmt(msiPendiente)}</b>
              </span>
            )}
            <span style={{ fontSize: 11, color: P.fijos.text, fontFamily: FF, fontWeight: 800 }}>
              Total: <b>{fmt(compromisoTotal)}</b>
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ══════════════════════════════════════════════
   SECCIÓN 6 — Timeline mensual compacto
═══════════════════════════════════════════════ */
function Timeline({ anualData, currentMonth, activeMonth, setActiveMonth, setView, setSection, mobile }) {
  return (
    <Card pad="0" style={{ overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "14px 18px", borderBottom: `1.5px solid ${P.border}`,
        fontSize: 13, fontWeight: 800, color: P.text, fontFamily: FF }}>
        Meses del año
      </div>
      {anualData.map((row, i) => {
        const hasData  = row.ingresos > 0 || row.fijos > 0;
        const ahorro   = row.ingresos - row.fijos - row.extras;
        const isHoy    = i === currentMonth;
        const isActive = i === activeMonth;

        return (
          <div
            key={i}
            onClick={() => { setActiveMonth(i); setView("mes"); setSection("resumen"); }}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: mobile ? "11px 14px" : "12px 18px",
              borderBottom: i < 11 ? `1px solid ${P.border}` : "none",
              cursor: "pointer",
              background: isActive ? P.ahorro.pastel : "transparent",
              transition: "background .15s",
            }}
          >
            {/* Mes */}
            <div style={{ width: mobile ? 36 : 96, flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: mobile ? 12 : 13, fontWeight: isHoy ? 900 : 600,
                color: isHoy ? P.ingresos.text : P.text, fontFamily: FF }}>
                {mobile ? MESES_CORTO[i] : MESES[i]}
              </span>
              {isHoy && (
                <span style={{ fontSize: 8, fontWeight: 800, color: P.ingresos.text,
                  background: P.ingresos.pastel, padding: "1px 5px", borderRadius: 99,
                  border: `1px solid ${P.ingresos.tint}`, lineHeight: 1.6 }}>
                  HOY
                </span>
              )}
            </div>

            {hasData ? (
              <>
                {/* Ingreso */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9, color: P.textXs, fontFamily: FF }}>Ingreso</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: P.ingresos.text, fontFamily: FF }}>
                    {fmt(row.ingresos)}
                  </div>
                </div>
                {/* Gasto */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9, color: P.textXs, fontFamily: FF }}>Gasto</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: P.extras.text, fontFamily: FF }}>
                    {fmt(row.fijos + row.extras)}
                  </div>
                </div>
                {/* Ahorro neto */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9, color: P.textXs, fontFamily: FF }}>Neto</div>
                  <div style={{ fontSize: 12, fontWeight: 900,
                    color: ahorro >= 0 ? P.ingresos.text : P.danger.text, fontFamily: FF }}>
                    {fmt(ahorro)}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, fontSize: 11, color: P.textXs, fontFamily: FF, fontStyle: "italic" }}>
                — sin datos —
              </div>
            )}

            {/* Chevron */}
            <div style={{ fontSize: 14, color: P.textXs, flexShrink: 0 }}>›</div>
          </div>
        );
      })}
    </Card>
  );
}

/* ══════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════ */
export default function AnualView() {
  const {
    anualData, anualTotals, currentMonth, data,
    totalDeudaTDCGlobal, recurrentes, msiCompras,
  } = useFinancial();
  const { activeMonth, setActiveMonth, setView, setSection } = useNavigation();
  const { mobile } = useUI();

  return (
    <div>
      {/* 1 — Scorecard Anual */}
      <ScoreAnual
        anualTotals={anualTotals}
        totalDeudaTDCGlobal={totalDeudaTDCGlobal}
        currentMonth={currentMonth}
        mobile={mobile}
      />

      {/* 2 — Tasa de ahorro por mes */}
      <TasaAhorro anualData={anualData} currentMonth={currentMonth} data={data} />

      {/* 3 — Top categorías */}
      <TopCategorias data={data} currentMonth={currentMonth} />

      {/* 4 — Tendencia gasto mensual */}
      <TendenciaGasto anualData={anualData} currentMonth={currentMonth} />

      {/* 5 — Proyección a diciembre */}
      <Proyeccion
        anualTotals={anualTotals}
        currentMonth={currentMonth}
        recurrentes={recurrentes}
        msiCompras={msiCompras}
      />

      {/* 6 — Timeline mensual compacto */}
      <Timeline
        anualData={anualData}
        currentMonth={currentMonth}
        activeMonth={activeMonth}
        setActiveMonth={setActiveMonth}
        setView={setView}
        setSection={setSection}
        mobile={mobile}
      />
    </div>
  );
}
